import { FilePenLine, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getEmailedDonors } from "../../../features/donorsSlice";
import ResourseButtons from "../../../components/form/buttons/ResourseButtons";
import { putIncludeDonor } from "../../../features/projectsSlice";
import { closeModal } from "../../../features/modalSLice";
import { useNotification } from "../../../components/notification/NotificationContainer";
import { formatCurrency } from "../../../util";

const IncludeDonor = ({ id }) => {
  const dispatch = useDispatch();
  const donors = useSelector((state) => state.donors.data);
  const project = useSelector((state) => state.projects.selected);
  const includeDonorLoad = useSelector(
    (state) => state.projects.load.putIncludeDonor
  );
  const addNotification = useNotification();
  const [budgetTotal, setBudgetTotal] = useState(0.0);
  const {
    control,
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "donors",
  });

  const watchDonors = (watch("donors") || []).map((item) => item.amount);

  const onSubmit = async (data) => {
    try {
      const res = await dispatch(putIncludeDonor({ id, data }));
      if (putIncludeDonor.fulfilled.match(res)) {
        dispatch(closeModal());
        addNotification("Donors added Successfully", "success");
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "warning");
    }
  };

  useEffect(() => {
    if (donors.length <= 0) {
      dispatch(getEmailedDonors({ id, data: { required: "true" } }));
    }
  }, []);

  useEffect(() => {
    if (donors.length > 0) {
      if (project.id) {
        reset({ donors: project.donors });
        setBudgetTotal(
          formatCurrency(
            project.budget
              .map((item) => item.amount * item.quantity)
              .reduce((acc, curr) => acc + curr, 0)
          )
        );
      }
    }
  }, [project, donors]);

  useEffect(() => {
    watch(fields.map((_, index) => `donors.[${index}].amount`));
  }, [fields, watch]);

  return (
    <div className="min-w-[85vw] sm:min-w-[35rem] lg:min-w-[50rem] max-w-4xl w-full text-text-1/80">
      <div className="flex  justify-right items-center gap-2 py-3 border-b border-bkg2 mb-4 text-text-1/80">
        <FilePenLine />
        <h4> Add Donors to Project </h4>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <p>
            Budget Total: {budgetTotal}{" "}
            <span>{` ${project.currency?.symbol || "N/A"}`}</span>
          </p>
        </div>
        <div className="flex items-center justify-between text-base p-3 relative">
          <label className="block mb-0.5 text-text-1/90 font-medium">
            Donors Involved
          </label>
          {errors && errors.message && (
            <p className="input-error text-xs text-red-500 absolute top-5 left-40  ">
              {errors.message}
            </p>
          )}
          <button
            type="button"
            className="header-btn"
            onClick={() => {
              if (fields.length <= donors.length - 1) {
                append({
                  amount: 0,
                  project_id: id,
                  donor_id: null,
                });
              }
            }}
          >
            <Plus size={20} />
          </button>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <td className="pl-2 p-1 py-2 bg-gray-600 text-text-gen ">No</td>
                <td className="p-1 py-2 bg-gray-600 text-text-gen ">Donor</td>
                <td className="p-1 py-2 bg-gray-600 text-text-gen text-center">
                  Amount
                </td>
                <td className="p-1 py-2 bg-gray-600 text-text-gen ">Action</td>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={index}>
                  <td className="p-1 pl-2">{index + 1}</td>
                  <td className="p-1 text-right">
                    <div className="invisible">
                      <input
                        type="hidden"
                        defaultValue={field.value}
                        {...register(`donors.[${index}].project_id`)}
                      />
                    </div>

                    <select
                      type="text"
                      defaultValue={field.value}
                      className="  w-full focus:outline-primary p-0.5 mx-1 bg-bkg2 hover:bg-text-bkg2"
                      {...register(`donors.[${index}].donor_id`)}
                    >
                      <option value="">-- Select Donor</option>
                      {donors.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-1  text-center">
                    <input
                      type="number"
                      defaultValue={field.valuue}
                      className="focus:outline-primary p-0.5 mx-1 bg-transparent hover:bg-text-bkg2 man-w-16 text-center"
                      min={0}
                      step={0.01}
                      {...register(`donors.[${index}].amount`, {
                        valueAsNumber: true,
                      })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-red-500 px-2 py-1 rounded "
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="pl-2 font-bold">
                  Total
                </td>

                <td className="font-bold">
                  {formatCurrency(
                    watchDonors.reduce((acc, curr) => acc + curr, 0)
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <ResourseButtons submitText="Save" loading={includeDonorLoad} />
      </form>
    </div>
  );
};

export default IncludeDonor;
